import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import type { PurchaseRequisitionView } from '@wodi/shared';
import { Button, DateField, LoadingBlock, Notice, PageHeader, Select, TextArea } from '@wodi/ui';
import { useState } from 'react';

import {
  createPurchaseOrder,
  createPurchaseRequisition,
  purchaseRequisitionQuery,
} from '../api/procurement.ts';
import { searchSuppliers, storesQuery } from '../api/stock.ts';
import { useOnline } from '../hooks/use-online.ts';
import { screen } from '../layout/screen.tsx';
import { LoadFailed, OfflineNote } from '../procurement/common.tsx';
import {
  lineProblems,
  linesTotalCents,
  PackLinesEditor,
  readPrice,
  type PackLine,
} from '../procurement/pack-lines.tsx';
import { errorText, newRequestKey } from '../stock/common.tsx';
import { formatCents, readQuantity } from '../stock/format.ts';
import styles from '../stock/stock.module.css';
import { toasts } from '../toasts.ts';

// New LPO and new purchase requisition (docs/plans/phase2-procurement.md §5.2, §5.4).

const orderRoute = getRouteApi('/_app/procurement/orders/new');

export function NewPurchaseOrderPage() {
  const { requisitionId } = orderRoute.useSearch();
  const requisition = useQuery({
    ...purchaseRequisitionQuery(requisitionId ?? ''),
    enabled: requisitionId !== undefined,
  });
  if (requisitionId !== undefined && requisition.isPending)
    return <LoadingBlock label="Loading the requisition" rows={3} />;
  if (requisition.isError)
    return (
      <LoadFailed
        error={requisition.error}
        fallback="The purchase requisition couldn't be loaded."
        onRetry={() => void requisition.refetch()}
      />
    );
  return <OrderForm requisition={requisition.data ?? null} />;
}

function fromRequisition(req: PurchaseRequisitionView): PackLine[] {
  return req.lines.map((l) => ({
    key: newRequestKey(),
    itemId: l.itemId,
    itemName: l.itemName,
    baseUnitDisplay: l.baseUnitDisplay,
    packs: [
      {
        code: l.packUnitCode,
        label: `${l.packUnitDisplay} (${String(l.baseUnitsPerPack)} ${l.baseUnitDisplay})`,
        baseUnitsPerPack: l.baseUnitsPerPack,
      },
    ],
    packUnitCode: l.packUnitCode,
    packsText: String(l.packs),
    priceText: '',
  }));
}

function OrderForm({ requisition }: { requisition: PurchaseRequisitionView | null }) {
  const online = useOnline();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const stores = useQuery(storesQuery);
  const suppliers = useQuery({
    queryKey: ['suppliers', 'active'],
    queryFn: () => searchSuppliers(''),
  });
  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [storeId, setStoreId] = useState<string | null>(requisition?.store.id ?? null);
  const [expectedOn, setExpectedOn] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [lines, setLines] = useState<PackLine[]>(() =>
    requisition === null ? [] : fromRequisition(requisition),
  );
  const [tried, setTried] = useState(false);
  const [requestKey] = useState(newRequestKey);

  const send = useMutation({
    mutationFn: () =>
      createPurchaseOrder({
        clientRequestId: requestKey,
        supplierId: supplierId ?? '',
        storeId: storeId ?? '',
        ...(requisition === null ? {} : { purchaseRequisitionId: requisition.id }),
        ...(expectedOn === null ? {} : { expectedOn }),
        ...(note.trim() === '' ? {} : { note: note.trim() }),
        lines: lines.map((l) => ({
          itemId: l.itemId,
          packUnitCode: l.packUnitCode,
          packs: readQuantity(l.packsText) ?? 0,
          packPrice: { currency: 'KES' as const, cents: (readPrice(l.priceText) ?? 0n).toString() },
        })),
      }),
    onSuccess: async (order) => {
      await queryClient.invalidateQueries({ queryKey: ['procurement'] });
      toasts.show({ title: 'LPO sent for approval', description: order.number });
      void navigate({ to: '/procurement/orders/$orderId', params: { orderId: order.id } });
    },
  });

  const valid =
    supplierId !== null &&
    storeId !== null &&
    lines.length > 0 &&
    lines.every((l) => lineProblems(l, true).length === 0);

  return (
    <div className={styles.page}>
      <p className={styles.meta}>
        <Link to="/procurement">Back to LPOs</Link>
      </p>
      <PageHeader title="New LPO" />
      {requisition === null ? null : (
        <p className={styles.meta}>
          From purchase requisition <span className="wodi-id">{requisition.number}</span>.
        </p>
      )}
      <form
        className={`${styles.wideForm} ${screen.panel}`}
        onSubmit={(e) => {
          e.preventDefault();
          setTried(true);
          if (valid) send.mutate();
        }}
      >
        <div className={styles.row}>
          <Select
            label="Supplier"
            placeholder={suppliers.isPending ? 'Loading suppliers…' : 'Choose a supplier'}
            options={(suppliers.data ?? []).map((s) => ({ id: s.id, label: s.name }))}
            value={supplierId}
            onChange={(v) => {
              setSupplierId(String(v));
            }}
            {...(tried && supplierId === null ? { errorMessage: 'Choose the supplier.' } : {})}
          />
          <Select
            label="Deliver to"
            placeholder={stores.isPending ? 'Loading stores…' : 'Choose a store'}
            options={(stores.data ?? []).map((s) => ({ id: s.id, label: s.name }))}
            value={storeId}
            isDisabled={requisition !== null}
            onChange={(v) => {
              setStoreId(String(v));
            }}
            {...(tried && storeId === null ? { errorMessage: 'Choose the store.' } : {})}
          />
          <DateField label="Expected on (optional)" value={expectedOn} onChange={setExpectedOn} />
        </div>
        {suppliers.isError ? (
          <Notice tone="error">
            {errorText(suppliers.error, "Suppliers couldn't be loaded.")}
          </Notice>
        ) : null}
        <PackLinesEditor lines={lines} onChange={setLines} withPrice tried={tried} />
        <TextArea label="Note (optional)" value={note} onChange={setNote} />
        <p className={styles.total}>Total {formatCents(linesTotalCents(lines).toString())}</p>
        {send.error === null ? null : (
          <Notice tone="error">{errorText(send.error, "The LPO wasn't sent. Try again.")}</Notice>
        )}
        <OfflineNote what="Sending an LPO" />
        <div className={styles.actions}>
          <Button
            type="submit"
            variant="primary"
            isDisabled={!online}
            isPending={send.isPending}
            pendingLabel="Sending…"
          >
            Send for approval
          </Button>
        </div>
      </form>
    </div>
  );
}

export function NewPurchaseRequisitionPage() {
  const online = useOnline();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const stores = useQuery(storesQuery);
  const [storeId, setStoreId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [lines, setLines] = useState<PackLine[]>([]);
  const [tried, setTried] = useState(false);
  const [requestKey] = useState(newRequestKey);
  const send = useMutation({
    mutationFn: () =>
      createPurchaseRequisition({
        clientRequestId: requestKey,
        storeId: storeId ?? '',
        ...(note.trim() === '' ? {} : { note: note.trim() }),
        lines: lines.map((l) => ({
          itemId: l.itemId,
          packUnitCode: l.packUnitCode,
          packs: readQuantity(l.packsText) ?? 0,
        })),
      }),
    onSuccess: async (req) => {
      await queryClient.invalidateQueries({ queryKey: ['procurement'] });
      toasts.show({ title: 'Purchase requested', description: req.number });
      void navigate({
        to: '/procurement/requisitions/$requisitionId',
        params: { requisitionId: req.id },
      });
    },
  });
  const valid =
    storeId !== null && lines.length > 0 && lines.every((l) => lineProblems(l, false).length === 0);
  return (
    <div className={styles.page}>
      <p className={styles.meta}>
        <Link to="/procurement/requisitions">Back to purchase requisitions</Link>
      </p>
      <PageHeader title="New purchase requisition" />
      <form
        className={`${styles.wideForm} ${screen.panel}`}
        onSubmit={(e) => {
          e.preventDefault();
          setTried(true);
          if (valid) send.mutate();
        }}
      >
        <Select
          label="For store"
          placeholder={stores.isPending ? 'Loading stores…' : 'Choose a store'}
          options={(stores.data ?? []).map((s) => ({ id: s.id, label: s.name }))}
          value={storeId}
          onChange={(v) => {
            setStoreId(String(v));
          }}
          {...(tried && storeId === null ? { errorMessage: 'Choose the store.' } : {})}
        />
        <PackLinesEditor lines={lines} onChange={setLines} withPrice={false} tried={tried} />
        <TextArea label="Note (optional)" value={note} onChange={setNote} />
        {send.error === null ? null : (
          <Notice tone="error">
            {errorText(send.error, "The requisition wasn't sent. Try again.")}
          </Notice>
        )}
        <OfflineNote what="Requesting a purchase" />
        <div className={styles.actions}>
          <Button
            type="submit"
            variant="primary"
            isDisabled={!online}
            isPending={send.isPending}
            pendingLabel="Sending…"
          >
            Request purchase
          </Button>
        </div>
      </form>
    </div>
  );
}
